import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import { fadeIn } from './animations';

// Filter UI component
const FilterSection = memo(
  ({
    colorOptions,
    categoryOptions,
    activeColorFilter,
    activeCategoryFilter,
    filterDropdownOpen,
    onSetColorFilter,
    onSetCategoryFilter,
    onClearAllFilters,
    onToggleFilterDropdown,
    filterRef,
  }) => {
    return (
      <motion.div variants={fadeIn} className="mb-10">
        <div className="flex justify-between items-center">
          {/* Combined Filter Dropdown */}
          <div className="relative" ref={filterRef}>
            <button
              onClick={() => onToggleFilterDropdown()}
              className="flex items-center px-6 py-2 rounded-soft bg-light/80 hover:bg-light hover:shadow-pastel-sm transition-all duration-300 text-sm font-medium"
              data-testid="filter-dropdown-button"
            >
              <FontAwesomeIcon
                icon={faFilter}
                className="text-secondary mr-2"
              />
              <span className="text-primary">Filters</span>
              <svg
                className="w-4 h-4 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </button>
            {filterDropdownOpen && (
              <div
                className="absolute left-0 mt-2 w-64 bg-white rounded-soft shadow-pastel-md z-50"
                data-testid="filter-dropdown-menu"
              >
                <div className="p-3 border-b border-light">
                  <h4 className="font-medium text-primary mb-2">Color</h4>
                  <div className="grid grid-cols-2 gap-1">
                    {colorOptions.map((color) => (
                      <button
                        key={color}
                        onClick={() => {
                          onSetColorFilter(color);
                          onToggleFilterDropdown(false);
                        }}
                        className={`text-left px-3 py-1.5 text-sm rounded-soft ${
                          activeColorFilter === color
                            ? 'bg-secondary/10 text-primary font-medium'
                            : 'text-text-dark hover:bg-light'
                        }`}
                        data-testid={`color-filter-${color}`}
                      >
                        {color}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="p-3">
                  <h4 className="font-medium text-primary mb-2">Category</h4>
                  <div className="grid grid-cols-2 gap-1">
                    {categoryOptions.map((category) => (
                      <button
                        key={category}
                        onClick={() => {
                          onSetCategoryFilter(category);
                          onToggleFilterDropdown(false);
                        }}
                        className={`text-left px-3 py-1.5 text-sm rounded-soft ${
                          activeCategoryFilter === category
                            ? 'bg-secondary/10 text-primary font-medium'
                            : 'text-text-dark hover:bg-light'
                        }`}
                        data-testid={`category-filter-${category}`}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                </div>
                {(activeColorFilter !== 'All' ||
                  activeCategoryFilter !== 'All') && (
                  <div className="p-3 border-t border-light">
                    <button
                      onClick={() => {
                        onClearAllFilters();
                      }}
                      className="w-full py-2 rounded-soft bg-primary/10 hover:bg-primary/20 text-primary text-sm font-medium transition-all duration-300"
                      data-testid="clear-all-filters-button"
                    >
                      Clear All Filters
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Active Filter Pills - Show when filters are active */}
          <div className="flex items-center gap-2">
            {activeColorFilter !== 'All' && (
              <div
                className="flex items-center px-3 py-1 rounded-full bg-secondary/10 text-primary text-xs"
                data-testid="active-color-filter-pill"
              >
                <span>Color: {activeColorFilter}</span>
                <button
                  onClick={() => onSetColorFilter('All')}
                  className="ml-2 text-primary/70 hover:text-primary"
                  aria-label={`Remove ${activeColorFilter} color filter`}
                  data-testid="clear-color-filter-button"
                >
                  ×
                </button>
              </div>
            )}

            {activeCategoryFilter !== 'All' && (
              <div
                className="flex items-center px-3 py-1 rounded-full bg-secondary/10 text-primary text-xs"
                data-testid="active-category-filter-pill"
              >
                <span>Category: {activeCategoryFilter}</span>
                <button
                  onClick={() => onSetCategoryFilter('All')}
                  className="ml-2 text-primary/70 hover:text-primary"
                  aria-label={`Remove ${activeCategoryFilter} category filter`}
                  data-testid="clear-category-filter-button"
                >
                  ×
                </button>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  }
);

export default FilterSection;
